import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box, Button, Grid, TextField, Typography, Paper, MenuItem, IconButton,
  CircularProgress, Switch, Chip, Collapse, Divider, Autocomplete, alpha, Fade, Grow,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InventoryIcon from "@mui/icons-material/Inventory";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ColorLensIcon from "@mui/icons-material/ColorLens";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import StorefrontIcon from "@mui/icons-material/Storefront";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import CategoryIcon from "@mui/icons-material/Category";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import SettingsIcon from "@mui/icons-material/Settings";
import QrCodeIcon from "@mui/icons-material/QrCode";
import ImageIcon from "@mui/icons-material/Image";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import TuneIcon from "@mui/icons-material/Tune";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import AddIcon from "@mui/icons-material/Add";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { useFormik } from "formik";
import { HexColorPicker } from "react-colorful";
import {
  useCreateProductMutation,
  useGetAllMainCategoryQuery,
  useGetModifierGroupQuery,
  useGetLastProductIdQuery,
  useGetProductBrandQuery,
  useGetProductTagQuery,
  useGetSubCategoryByArrayQuery,
} from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import {
  ICategory, ILastProductId, IModifierGroupPopUp,
  IProduct, IProductBrand, IProductTag,
} from "../../../Api/Interface/api.interface";
import { uploadImage } from "../../../Components/UploadFile/upload";
import { useNavigate } from "react-router";

const API_URL = (process.env.REACT_APP_API_URL || "").trim();

const labelSx = {
  fontSize: 12, fontWeight: 600, color: "#475569", mb: 0.75,
  letterSpacing: "0.02em", textTransform: "uppercase" as const,
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2.5, fontSize: 13, bgcolor: "#fff",
    transition: "all 0.2s ease",
    "& fieldset": { borderColor: "#e2e8f0", transition: "all 0.2s ease" },
    "&:hover fieldset": { borderColor: "#94a3b8" },
    "&.Mui-focused fieldset": { borderColor: "#00838f", borderWidth: 2 },
    "&.Mui-focused": { boxShadow: "0 0 0 3px rgba(0,131,143,0.08)" },
  },
};

const Section = ({
  icon, title, badge, children, collapsible, defaultOpen = true, accentColor = "#00838f", subtitle,
}: {
  icon: React.ReactNode; title: string; badge?: string; children: React.ReactNode;
  collapsible?: boolean; defaultOpen?: boolean; accentColor?: string; subtitle?: string;
}) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Paper
      elevation={0}
      sx={{
        mb: 3, borderRadius: 3, border: "1px solid #e2e8f0",
        overflow: "hidden", transition: "all 0.3s ease",
        "&:hover": { borderColor: "#cbd5e1", boxShadow: "0 4px 20px rgba(0,0,0,0.04)" },
      }}
    >
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(accentColor, 0.04)} 0%, ${alpha(accentColor, 0.01)} 100%)`,
          borderBottom: open ? `1px solid ${alpha(accentColor, 0.08)}` : "none",
          px: 3, py: 2.5,
          cursor: collapsible ? "pointer" : "default",
          transition: "all 0.2s ease",
          "&:hover": collapsible ? { background: `linear-gradient(135deg, ${alpha(accentColor, 0.06)} 0%, ${alpha(accentColor, 0.02)} 100%)` } : {},
        }}
        onClick={() => collapsible && setOpen(v => !v)}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center",
            background: `linear-gradient(135deg, ${alpha(accentColor, 0.12)} 0%, ${alpha(accentColor, 0.06)} 100%)`,
          }}>
            {icon}
          </Box>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography fontSize={15} fontWeight={700} color="#0f172a" letterSpacing="-0.01em">{title}</Typography>
              {badge && (
                <Chip label={badge} size="small" sx={{
                  height: 20, fontSize: 9, fontWeight: 700,
                  background: "linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)",
                  color: "#dc2626", border: "none",
                }} />
              )}
            </Box>
            {subtitle && <Typography fontSize={11} color="#94a3b8" mt={0.25}>{subtitle}</Typography>}
          </Box>
          {collapsible && (
            <ExpandMoreIcon sx={{
              fontSize: 20, color: "#94a3b8",
              transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              transform: open ? "rotate(180deg)" : "rotate(0)",
            }} />
          )}
        </Box>
      </Box>
      {collapsible ? (
        <Collapse in={open}>
          <Box sx={{ p: 3 }}>{children}</Box>
        </Collapse>
      ) : (
        <Box sx={{ p: 3 }}>{children}</Box>
      )}
    </Paper>
  );
};

const Index = () => {
  const navigate = useNavigate();
  const [newProduct, { isLoading }] = useCreateProductMutation();
  const { showErrorMessage, showMessage } = useNotifier();

  // Data queries
  const { data: categoryData } = useGetAllMainCategoryQuery({ departmentId: "", coursingId: "", roleId: null, taxeId: "", createdDateStart: null, createdDateEnd: null, searchText: "", numberOfLength: 0, isActive: true, categoryIsLooping: null, createdDate: null });
  const { data: productBrandData } = useGetProductBrandQuery({ searchText: "", isActive: true });
  const { data: productTagData } = useGetProductTagQuery({ searchText: "", isActive: true });
  const { data: modifierGroupData } = useGetModifierGroupQuery({ productBrandId: "", productCategoryId: "", productTagIds: [], modifierViewOnline: "", createdDateStart: null, createdDateEnd: null, searchText: "", createdDate: "", id: null });
  const { data: newLastProductIdData } = useGetLastProductIdQuery();

  // Category state
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [selectedSubCategoryId, setSelectedSubCategoryId] = useState<number | null>(null);
  const [selectedSubToSubCategoryId, setSelectedSubToSubCategoryId] = useState<number | null>(null);
  const [selectedSubToSubToSubCategoryId, setSelectedSubToSubToSubCategoryId] = useState<number | null>(null);

  const { data: subCategoryData } = useGetSubCategoryByArrayQuery({ parent_ids: selectedCategoryId ? [Number(selectedCategoryId)] : [] });
  const { data: subCategory2Data } = useGetSubCategoryByArrayQuery({ parent_ids: selectedSubCategoryId ? [Number(selectedCategoryId), Number(selectedSubCategoryId)] : [] });
  const { data: subCategory3Data } = useGetSubCategoryByArrayQuery({ parent_ids: selectedSubCategoryId ? [Number(selectedCategoryId), Number(selectedSubCategoryId), Number(selectedSubToSubCategoryId)] : [] });

  const categoryList = useMemo(() => categoryData?.data as ICategory[] || [], [categoryData?.data]);
  const subCategoryList = useMemo(() => subCategoryData?.data as ICategory[] || [], [subCategoryData?.data]);
  const subToSubCategoryList = useMemo(() => subCategory2Data?.data as ICategory[] || [], [subCategory2Data?.data]);
  const subToSubToSubCategoryList = useMemo(() => subCategory3Data?.data as ICategory[] || [], [subCategory3Data?.data]);
  const productBrandList = useMemo(() => productBrandData?.data as IProductBrand[] || [], [productBrandData?.data]);
  const productTagList = useMemo(() => productTagData?.data as IProductTag[] || [], [productTagData?.data]);
  const modifierGroupList = useMemo(() => modifierGroupData?.data as IModifierGroupPopUp[] || [], [modifierGroupData?.data]);

  // UI state
  const [selectedImage, setSelectedImage] = useState<File | undefined>(undefined);
  const [selectedColor, setSelectedColor] = useState("#FFFFFF");
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const colorRef = useRef<HTMLDivElement>(null);

  // Supplier state
  const [suppliers, setSuppliers] = useState<{ id: number; customerCompanyName: string }[]>([]);
  const [supplierMappings, setSupplierMappings] = useState<{ supplier_id: number; supplier_price: number; supplier_unit: string; is_default: boolean }[]>([]);

  useEffect(() => {
    (async () => {
      try {
        const t = localStorage.getItem("token") || "";
        const res = await fetch(`${API_URL}/vendor/getAll`, { method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json", Authorization: `Bearer ${t}` }, body: JSON.stringify({ searchText: "" }) });
        const json = await res.json();
        setSuppliers((json?.data || []).filter((s: any) => !s.isDelete).map((s: any) => ({ id: s.id, customerCompanyName: s.customerCompanyName || `Supplier #${s.id}` })));
      } catch {}
    })();
  }, []);

  const formik = useFormik<IProduct>({
    initialValues: {
      productName: "", modelNo: "", productCategoryId: "", mainCatId: "",
      productDetailsIsLooping: false, productsDetailsLoopingConstant: "",
      productShortDescription: "", productLongDescription: "",
      productConversionUnit: [], saleTypeId: [], stockManagementType: String(2),
      productBrandId: "", productTagIds: [], productViewOnline: true,
      productViewPos: false, isActive: true, isSerialize: false, isModifier: false,
      productPrinterIds: [], productIcon: "", productButtonColor: "",
      productBarcode: "", productManualBarcode: "", productCanSell: true,
      productImg: "", productPrice: "", hasVariants: false, variantIds: "",
      salePrice: "", offerPrice: "", wholesaleprice: "", qty: 0, orderNumber: "",
      modifierGroupId: [],
    },
    onSubmit: async (values, { resetForm }) => {
      try {
        const productCategoryId = selectedSubToSubToSubCategoryId || selectedSubToSubCategoryId || selectedSubCategoryId || selectedCategoryId || values.mainCatId;

        let productImg = "";
        if (selectedImage) productImg = (await uploadImage(selectedImage)) || "";

        const temData = {
          productName: values.productName, productCategoryId,
          productDetailsIsLooping: values.productDetailsIsLooping,
          productsDetailsLoopingConstant: values.productsDetailsLoopingConstant,
          productShortDescription: values.productShortDescription,
          productLongDescription: values.productLongDescription,
          productConversionUnit: values.productConversionUnit,
          productBrandId: values.productBrandId, productTagIds: values.productTagIds,
          productViewOnline: values.productViewOnline, productViewPos: values.productViewPos,
          isActive: values.isActive, productPrinterIds: values.productPrinterIds,
          productIcon: values.productIcon, productButtonColor: selectedColor,
          productBarcode: values.productBarcode, productManualBarcode: values.productManualBarcode,
          productCanSell: values.productCanSell, productPrice: values.productPrice,
          saleTypeId: values.saleTypeId, stockManagementType: values.stockManagementType,
          modelNo: values.modelNo, isSerialize: values.isSerialize,
          isModifier: values.isModifier, productImg,
          hasVariants: values.hasVariants, variantIds: values.variantIds || "",
          salePrice: values.salePrice, offerPrice: values.offerPrice,
          wholesaleprice: values.wholesaleprice, qty: values.qty,
          orderNumber: values.orderNumber, modifierGroupId: values.modifierGroupId,
        };

        const res = await newProduct(temData).unwrap();
        if (!res.status) { showErrorMessage(res.message); return; }
        showMessage(res.message);

        // Save supplier mappings if any
        if (supplierMappings.length > 0 && res.data?.id) {
          const t = localStorage.getItem("token") || "";
          const headers = { "Content-Type": "application/json", Accept: "application/json", Authorization: `Bearer ${t}` };
          for (const m of supplierMappings) {
            try {
              await fetch(`${API_URL}/vendor/${m.supplier_id}/products`, {
                method: "POST", headers,
                body: JSON.stringify({ product_id: res.data.id, supplier_price: m.supplier_price, supplier_unit: m.supplier_unit, is_default: m.is_default }),
              });
            } catch {}
          }
        }

        // Save inventory settings if tracking
        if (inventoryForm.track_inventory && res.data?.id) {
          const t = localStorage.getItem("token") || "";
          try {
            await fetch(`${API_URL}/warehouse/product/${res.data.id}/settings`, {
              method: "PUT",
              headers: { "Content-Type": "application/json", Accept: "application/json", Authorization: `Bearer ${t}` },
              body: JSON.stringify(inventoryForm),
            });
          } catch {}
        }

        resetForm(); setSelectedImage(undefined); setSelectedCategoryId(null);
        setSelectedSubCategoryId(null); setSelectedSubToSubCategoryId(null);
        setSelectedSubToSubToSubCategoryId(null); setSupplierMappings([]);
        navigate("/product/list");
      } catch { showErrorMessage("Something went wrong"); }
    },
  });

  // Inventory form
  const [inventoryForm, setInventoryForm] = useState({ track_inventory: false, current_stock: 0, minimum_stock: 0, reorder_point: 0, reorder_qty: 0 });

  const formValid = useMemo(() => formik.values.productName.trim() !== "", [formik.values.productName]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/jpg", "image/gif"].includes(file.type)) { showErrorMessage("Must be an image"); return; }
    if (file.size > 2048 * 1024) { showErrorMessage("Must be under 2 MB"); return; }
    setSelectedImage(file);
  };

  const addSupplierMapping = () => {
    setSupplierMappings(prev => [...prev, { supplier_id: 0, supplier_price: 0, supplier_unit: "", is_default: prev.length === 0 }]);
  };

  // Category breadcrumb for visual feedback
  const categoryBreadcrumb = useMemo(() => {
    const parts: string[] = [];
    if (selectedCategoryId) {
      const cat = categoryList.find(c => c.id === selectedCategoryId);
      if (cat) parts.push(cat.categoryName);
    }
    if (selectedSubCategoryId) {
      const cat = subCategoryList.find(c => c.id === selectedSubCategoryId);
      if (cat) parts.push(cat.categoryName);
    }
    if (selectedSubToSubCategoryId) {
      const cat = subToSubCategoryList.find(c => c.id === selectedSubToSubCategoryId);
      if (cat) parts.push(cat.categoryName);
    }
    if (selectedSubToSubToSubCategoryId) {
      const cat = subToSubToSubCategoryList.find(c => c.id === selectedSubToSubToSubCategoryId);
      if (cat) parts.push(cat.categoryName);
    }
    return parts;
  }, [selectedCategoryId, selectedSubCategoryId, selectedSubToSubCategoryId, selectedSubToSubToSubCategoryId, categoryList, subCategoryList, subToSubCategoryList, subToSubToSubCategoryList]);

  return (
    <Box sx={{ bgcolor: "#f1f5f9", minHeight: "100vh" }}>
      {/* ── Top Bar ── */}
      <Box sx={{
        background: "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)",
        px: 3, py: 0, position: "sticky", top: 0, zIndex: 10,
        boxShadow: "0 4px 30px rgba(0,0,0,0.15)",
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mx: "auto", py: 2 }}>
          <IconButton onClick={() => navigate("/product/list")} size="small"
            sx={{
              border: "1px solid rgba(255,255,255,0.15)", borderRadius: 2,
              width: 38, height: 38, backdropFilter: "blur(10px)",
              bgcolor: "rgba(255,255,255,0.05)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.12)", borderColor: "rgba(255,255,255,0.3)" },
              transition: "all 0.2s ease",
            }}>
            <ArrowBackIcon sx={{ fontSize: 18, color: "#e2e8f0" }} />
          </IconButton>
          <Divider orientation="vertical" flexItem sx={{ borderColor: "rgba(255,255,255,0.1)" }} />
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={{
                width: 32, height: 32, borderRadius: 1.5, display: "flex", alignItems: "center", justifyContent: "center",
                background: "linear-gradient(135deg, #00838f 0%, #00acc1 100%)",
                boxShadow: "0 2px 8px rgba(0,131,143,0.4)",
              }}>
                <ShoppingCartIcon sx={{ fontSize: 16, color: "#fff" }} />
              </Box>
              <Typography fontSize={18} fontWeight={800} color="#fff" letterSpacing="-0.02em">
                New Product
              </Typography>
            </Box>
            <Typography fontSize={11} color="rgba(255,255,255,0.45)" mt={0.25} ml={5.5}>
              Fill in the details to create a new product
            </Typography>
          </Box>
          <Button variant="outlined" size="small" onClick={() => navigate("/product/list")}
            sx={{
              borderRadius: 2.5, textTransform: "none", fontSize: 12, fontWeight: 600,
              borderColor: "rgba(255,255,255,0.15)", color: "#94a3b8", px: 2.5,
              "&:hover": { borderColor: "rgba(255,255,255,0.3)", bgcolor: "rgba(255,255,255,0.05)", color: "#e2e8f0" },
              transition: "all 0.2s ease",
            }}>
            Cancel
          </Button>
          <Button variant="contained" disableElevation size="small" disabled={!formValid || isLoading}
            onClick={() => formik.handleSubmit()}
            startIcon={isLoading ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <SaveIcon sx={{ fontSize: 16 }} />}
            sx={{
              borderRadius: 2.5, textTransform: "none", fontSize: 12, fontWeight: 700, px: 3,
              background: "linear-gradient(135deg, #00838f 0%, #00acc1 100%)",
              boxShadow: "0 2px 12px rgba(0,131,143,0.4)",
              "&:hover": { background: "linear-gradient(135deg, #006064 0%, #00838f 100%)", boxShadow: "0 4px 20px rgba(0,131,143,0.5)" },
              "&.Mui-disabled": { background: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.25)" },
              transition: "all 0.2s ease",
            }}>
            {isLoading ? "Saving..." : "Save Product"}
          </Button>
        </Box>
      </Box>

      {/* ── Content ── */}
      <Box sx={{ mx: "auto", p: 3 }}>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={3}>
            {/* LEFT COLUMN */}
            <Grid item xs={12} lg={8}>

              {/* A. Basic Information */}
              <Section
                icon={<CategoryIcon sx={{ fontSize: 18, color: "#3b82f6" }} />}
                title="Basic Information"
                badge="Required"
                accentColor="#3b82f6"
                subtitle="Product name, category, and description"
              >
                <Grid container spacing={2.5}>
                  <Grid item xs={12}>
                    <Typography sx={labelSx}>Product Name *</Typography>
                    <TextField
                      fullWidth placeholder="Enter product name"
                      {...formik.getFieldProps("productName")}
                      sx={{
                        ...inputSx,
                        "& .MuiOutlinedInput-root": {
                          ...inputSx["& .MuiOutlinedInput-root"],
                          fontSize: 15, fontWeight: 600,
                        },
                      }}
                    />
                  </Grid>

                  {/* Category Hierarchy - Conditional Rendering */}
                  <Grid item xs={12}>
                    <Box sx={{
                      p: 2.5, borderRadius: 3,
                      background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                      border: "1px solid #e2e8f0",
                    }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                        <AccountTreeIcon sx={{ fontSize: 16, color: "#00838f" }} />
                        <Typography fontSize={12} fontWeight={700} color="#334155" letterSpacing="0.02em" textTransform="uppercase">
                          Category Hierarchy
                        </Typography>
                      </Box>

                      {/* Breadcrumb trail */}
                      {categoryBreadcrumb.length > 0 && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 2, flexWrap: "wrap" }}>
                          {categoryBreadcrumb.map((name, idx) => (
                            <React.Fragment key={idx}>
                              {idx > 0 && (
                                <Typography fontSize={11} color="#94a3b8" sx={{ mx: 0.5 }}>/</Typography>
                              )}
                              <Chip
                                label={name}
                                size="small"
                                sx={{
                                  height: 24, fontSize: 11, fontWeight: 600,
                                  bgcolor: idx === categoryBreadcrumb.length - 1 ? alpha("#00838f", 0.1) : "#fff",
                                  color: idx === categoryBreadcrumb.length - 1 ? "#00838f" : "#64748b",
                                  border: `1px solid ${idx === categoryBreadcrumb.length - 1 ? alpha("#00838f", 0.2) : "#e2e8f0"}`,
                                  textTransform: "capitalize",
                                }}
                              />
                            </React.Fragment>
                          ))}
                        </Box>
                      )}

                      <Grid container spacing={2}>
                        {/* Main Category - always visible */}
                        <Grid item xs={12} sm={subCategoryList.length > 0 ? 6 : 12}>
                          <Typography sx={{ ...labelSx, fontSize: 11 }}>Category</Typography>
                          <TextField
                            select fullWidth size="small"
                            value={selectedCategoryId || ""}
                            onChange={e => {
                              setSelectedCategoryId(Number(e.target.value));
                              setSelectedSubCategoryId(null);
                              setSelectedSubToSubCategoryId(null);
                              setSelectedSubToSubToSubCategoryId(null);
                            }}
                            sx={inputSx}
                          >
                            <MenuItem value="">Select Category</MenuItem>
                            {categoryList?.map(c => (
                              <MenuItem key={c.id} value={c.id} sx={{ textTransform: "capitalize" }}>{c.categoryName}</MenuItem>
                            ))}
                          </TextField>
                        </Grid>

                        {/* Sub Category - only if subcategories exist */}
                        {subCategoryList.length > 0 && (
                          <Grid item xs={12} sm={6}>
                            <Fade in={subCategoryList.length > 0}>
                              <Box>
                                <Typography sx={{ ...labelSx, fontSize: 11 }}>Sub Category</Typography>
                                <TextField
                                  select fullWidth size="small"
                                  value={selectedSubCategoryId || ""}
                                  onChange={e => {
                                    setSelectedSubCategoryId(Number(e.target.value));
                                    setSelectedSubToSubCategoryId(null);
                                    setSelectedSubToSubToSubCategoryId(null);
                                  }}
                                  sx={inputSx}
                                >
                                  <MenuItem value="">Select Sub Category</MenuItem>
                                  {subCategoryList.map(c => (
                                    <MenuItem key={c.id} value={c.id} sx={{ textTransform: "capitalize" }}>{c.categoryName}</MenuItem>
                                  ))}
                                </TextField>
                              </Box>
                            </Fade>
                          </Grid>
                        )}

                        {/* Sub Sub Category - only if sub-subcategories exist */}
                        {subToSubCategoryList.length > 0 && (
                          <Grid item xs={12} sm={6}>
                            <Fade in={subToSubCategoryList.length > 0}>
                              <Box>
                                <Typography sx={{ ...labelSx, fontSize: 11 }}>Sub Sub Category</Typography>
                                <TextField
                                  select fullWidth size="small"
                                  value={selectedSubToSubCategoryId || ""}
                                  onChange={e => {
                                    setSelectedSubToSubCategoryId(Number(e.target.value));
                                    setSelectedSubToSubToSubCategoryId(null);
                                  }}
                                  sx={inputSx}
                                >
                                  <MenuItem value="">Select</MenuItem>
                                  {subToSubCategoryList.map(c => (
                                    <MenuItem key={c.id} value={c.id} sx={{ textTransform: "capitalize" }}>{c.categoryName}</MenuItem>
                                  ))}
                                </TextField>
                              </Box>
                            </Fade>
                          </Grid>
                        )}

                        {/* Sub Sub Sub Category - only if they exist */}
                        {subToSubToSubCategoryList.length > 0 && (
                          <Grid item xs={12} sm={6}>
                            <Fade in={subToSubToSubCategoryList.length > 0}>
                              <Box>
                                <Typography sx={{ ...labelSx, fontSize: 11 }}>Sub Sub Sub Category</Typography>
                                <TextField
                                  select fullWidth size="small"
                                  value={selectedSubToSubToSubCategoryId || ""}
                                  onChange={e => {
                                    setSelectedSubToSubToSubCategoryId(Number(e.target.value));
                                  }}
                                  sx={inputSx}
                                >
                                  <MenuItem value="">Select</MenuItem>
                                  {subToSubToSubCategoryList.map(c => (
                                    <MenuItem key={c.id} value={c.id} sx={{ textTransform: "capitalize" }}>{c.categoryName}</MenuItem>
                                  ))}
                                </TextField>
                              </Box>
                            </Fade>
                          </Grid>
                        )}
                      </Grid>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography sx={labelSx}>Brand</Typography>
                    <TextField select fullWidth size="small" {...formik.getFieldProps("productBrandId")} sx={inputSx}>
                      <MenuItem value="">No Brand</MenuItem>
                      {productBrandList?.map(b => <MenuItem key={b.id} value={b.id}>{b.productBrandName}</MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={labelSx}>Model / SKU</Typography>
                    <TextField fullWidth size="small" placeholder="Enter model number" {...formik.getFieldProps("modelNo")} sx={inputSx} />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography sx={labelSx}>Short Description</Typography>
                    <TextField
                      fullWidth size="small" multiline rows={3}
                      placeholder="Brief product description..."
                      {...formik.getFieldProps("productShortDescription")} sx={inputSx}
                    />
                  </Grid>
                </Grid>
              </Section>

              {/* B. Pricing */}
              <Section
                icon={<AttachMoneyIcon sx={{ fontSize: 18, color: "#f59e0b" }} />}
                title="Pricing"
                accentColor="#f59e0b"
                subtitle="Set cost, wholesale, and retail prices"
              >
                <Grid container spacing={2.5}>
                  {[
                    { label: "Cost Price", field: "productPrice", icon: "$", color: "#ef4444", hint: "Your purchase cost" },
                    { label: "Wholesale Price", field: "wholesaleprice", icon: "$", color: "#8b5cf6", hint: "Bulk buyer price" },
                    { label: "Instore (POS) Price", field: "salePrice", icon: "$", color: "#16a34a", hint: "Retail selling price" },
                  ].map((p) => (
                    <Grid item xs={12} sm={4} key={p.field}>
                      <Box sx={{
                        p: 2, borderRadius: 2.5, border: "1px solid #e2e8f0",
                        transition: "all 0.2s ease",
                        "&:hover": { borderColor: alpha(p.color, 0.3), boxShadow: `0 2px 12px ${alpha(p.color, 0.08)}` },
                      }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                          <Box sx={{
                            width: 28, height: 28, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                            bgcolor: alpha(p.color, 0.08),
                          }}>
                            <Typography fontSize={13} fontWeight={800} color={p.color}>{p.icon}</Typography>
                          </Box>
                          <Box>
                            <Typography fontSize={12} fontWeight={700} color="#334155">{p.label}</Typography>
                            <Typography fontSize={10} color="#94a3b8">{p.hint}</Typography>
                          </Box>
                        </Box>
                        <TextField
                          fullWidth size="small" type="number" placeholder="0.00"
                          {...formik.getFieldProps(p.field)}
                          sx={{
                            ...inputSx,
                            "& .MuiOutlinedInput-root": {
                              ...inputSx["& .MuiOutlinedInput-root"],
                              fontSize: 18, fontWeight: 700,
                            },
                          }}
                        />
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Section>

              {/* C. Units & Conversion */}
              <Section
                icon={<LocalShippingIcon sx={{ fontSize: 18, color: "#8b5cf6" }} />}
                title="Units & Conversion"
                accentColor="#8b5cf6"
                subtitle="Define measurement units and conversion rates"
                collapsible
                defaultOpen={false}
              >
                <Grid container spacing={2.5}>
                  <Grid item xs={12} sm={4}>
                    <Typography sx={labelSx}>Base Unit</Typography>
                    <TextField select fullWidth size="small" value={formik.values.productConversionUnit?.[0] || ""} onChange={e => formik.setFieldValue("productConversionUnit", e.target.value ? [e.target.value] : [])} sx={inputSx}>
                      <MenuItem value="">Select</MenuItem>
                      {["lb", "kg", "oz", "g", "unit", "each", "bunch", "dozen"].map(u => <MenuItem key={u} value={u}>{u}</MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography sx={labelSx}>Purchase Unit</Typography>
                    <TextField fullWidth size="small" placeholder="e.g. box, bag, crate" {...formik.getFieldProps("productsDetailsLoopingConstant")} sx={inputSx} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography sx={labelSx}>Conversion</Typography>
                    <TextField fullWidth size="small" placeholder="e.g. 1 box = 10 lb" {...formik.getFieldProps("orderNumber")} sx={inputSx} />
                  </Grid>
                </Grid>
              </Section>

              {/* D. Inventory Control */}
              <Section
                icon={<InventoryIcon sx={{ fontSize: 18, color: "#16a34a" }} />}
                title="Inventory Control"
                accentColor="#16a34a"
                subtitle="Stock tracking and reorder settings"
                collapsible
                defaultOpen={false}
              >
                <Box sx={{
                  display: "flex", alignItems: "center", gap: 1.5, mb: 2,
                  p: 2, borderRadius: 2.5,
                  bgcolor: inventoryForm.track_inventory ? alpha("#16a34a", 0.04) : "#f8fafc",
                  border: `1px solid ${inventoryForm.track_inventory ? alpha("#16a34a", 0.15) : "#e2e8f0"}`,
                  transition: "all 0.3s ease",
                }}>
                  <Switch size="small" checked={inventoryForm.track_inventory} onChange={(_, v) => setInventoryForm(f => ({ ...f, track_inventory: v }))}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": { color: "#16a34a" },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: "#16a34a" },
                    }}
                  />
                  <Box>
                    <Typography fontSize={13} fontWeight={600} color={inventoryForm.track_inventory ? "#16a34a" : "#94a3b8"}>
                      {inventoryForm.track_inventory ? "Inventory Tracking Enabled" : "Enable Inventory Tracking"}
                    </Typography>
                    <Typography fontSize={11} color="#94a3b8">
                      Monitor stock levels and get reorder alerts
                    </Typography>
                  </Box>
                </Box>
                <Collapse in={inventoryForm.track_inventory}>
                  <Grid container spacing={2.5}>
                    {[
                      { label: "Current Stock", key: "current_stock", color: "#3b82f6" },
                      { label: "Minimum Stock", key: "minimum_stock", color: "#f59e0b" },
                      { label: "Reorder Point", key: "reorder_point", color: "#ef4444" },
                      { label: "Reorder Qty", key: "reorder_qty", color: "#8b5cf6" },
                    ].map((inv) => (
                      <Grid item xs={6} sm={3} key={inv.key}>
                        <Box sx={{
                          p: 1.5, borderRadius: 2, border: "1px solid #e2e8f0",
                          borderTop: `3px solid ${inv.color}`,
                        }}>
                          <Typography sx={{ ...labelSx, fontSize: 10 }}>{inv.label}</Typography>
                          <TextField
                            fullWidth size="small" type="number"
                            value={(inventoryForm as any)[inv.key]}
                            onChange={e => setInventoryForm(f => ({ ...f, [inv.key]: Number(e.target.value) }))}
                            sx={inputSx}
                          />
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Collapse>
              </Section>

              {/* E. Supplier Mapping */}
              <Section
                icon={<StorefrontIcon sx={{ fontSize: 18, color: "#ea580c" }} />}
                title="Supplier Mapping"
                accentColor="#ea580c"
                subtitle="Link suppliers with pricing and units"
                collapsible
                defaultOpen={false}
              >
                {supplierMappings.length === 0 && (
                  <Box sx={{
                    textAlign: "center", py: 4,
                    borderRadius: 2.5, border: "2px dashed #e2e8f0",
                    background: "linear-gradient(135deg, #fafbfc 0%, #f8fafc 100%)",
                  }}>
                    <StorefrontIcon sx={{ fontSize: 36, color: "#cbd5e1", mb: 1 }} />
                    <Typography fontSize={13} color="#94a3b8" fontWeight={500}>No suppliers linked yet</Typography>
                    <Typography fontSize={11} color="#cbd5e1" mb={2}>Add suppliers to track purchase costs</Typography>
                    <Button size="small" variant="outlined" onClick={addSupplierMapping}
                      startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                      sx={{
                        borderRadius: 2, textTransform: "none", fontSize: 12, fontWeight: 600,
                        borderColor: "#ea580c", color: "#ea580c",
                        "&:hover": { borderColor: "#ea580c", bgcolor: alpha("#ea580c", 0.04) },
                      }}>
                      Add Supplier
                    </Button>
                  </Box>
                )}
                {supplierMappings.map((m, i) => (
                  <Grow in key={i}>
                    <Box sx={{
                      display: "flex", gap: 1.5, alignItems: "flex-end", mb: 2, p: 2.5,
                      borderRadius: 2.5, bgcolor: "#fafbfc",
                      border: m.is_default ? `1px solid ${alpha("#16a34a", 0.2)}` : "1px solid #e2e8f0",
                      transition: "all 0.2s ease",
                      "&:hover": { boxShadow: "0 2px 12px rgba(0,0,0,0.04)" },
                    }}>
                      <Box sx={{ flex: 2 }}>
                        <Typography sx={{ ...labelSx, fontSize: 10 }}>Supplier</Typography>
                        <TextField select fullWidth size="small" value={m.supplier_id || ""} onChange={e => { const v = [...supplierMappings]; v[i].supplier_id = Number(e.target.value); setSupplierMappings(v); }} sx={inputSx}>
                          <MenuItem value="">Select Supplier</MenuItem>
                          {suppliers.map(s => <MenuItem key={s.id} value={s.id}>{s.customerCompanyName}</MenuItem>)}
                        </TextField>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ ...labelSx, fontSize: 10 }}>Purchase Price</Typography>
                        <TextField fullWidth size="small" type="number" value={m.supplier_price} onChange={e => { const v = [...supplierMappings]; v[i].supplier_price = Number(e.target.value); setSupplierMappings(v); }} sx={inputSx} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ ...labelSx, fontSize: 10 }}>Unit</Typography>
                        <TextField fullWidth size="small" placeholder="box, lb" value={m.supplier_unit} onChange={e => { const v = [...supplierMappings]; v[i].supplier_unit = e.target.value; setSupplierMappings(v); }} sx={inputSx} />
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, pb: 0.5 }}>
                        <Chip label={m.is_default ? "Preferred" : "Alt"} size="small" onClick={() => { const v = supplierMappings.map((x, j) => ({ ...x, is_default: j === i })); setSupplierMappings(v); }}
                          sx={{
                            height: 24, fontSize: 10, fontWeight: 700, cursor: "pointer",
                            bgcolor: m.is_default ? alpha("#16a34a", 0.08) : "#f1f5f9",
                            color: m.is_default ? "#16a34a" : "#94a3b8",
                            border: `1px solid ${m.is_default ? alpha("#16a34a", 0.2) : "transparent"}`,
                            "&:hover": { bgcolor: m.is_default ? alpha("#16a34a", 0.12) : "#e2e8f0" },
                          }} />
                        <IconButton size="small" onClick={() => setSupplierMappings(prev => prev.filter((_, j) => j !== i))}
                          sx={{
                            width: 28, height: 28,
                            "&:hover": { bgcolor: alpha("#ef4444", 0.08) },
                          }}>
                          <DeleteOutlineIcon sx={{ fontSize: 16, color: "#ef4444" }} />
                        </IconButton>
                      </Box>
                    </Box>
                  </Grow>
                ))}
                {supplierMappings.length > 0 && (
                  <Button size="small" onClick={addSupplierMapping}
                    startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                    sx={{
                      textTransform: "none", fontSize: 12, fontWeight: 600, color: "#ea580c",
                      "&:hover": { bgcolor: alpha("#ea580c", 0.04) },
                    }}>
                    Add Another Supplier
                  </Button>
                )}
              </Section>
            </Grid>

            {/* RIGHT COLUMN */}
            <Grid item xs={12} lg={4}>

              {/* Product Image */}
              <Paper elevation={0} sx={{
                mb: 3, borderRadius: 3, border: "1px solid #e2e8f0",
                overflow: "hidden",
                "&:hover": { borderColor: "#cbd5e1", boxShadow: "0 4px 20px rgba(0,0,0,0.04)" },
                transition: "all 0.3s ease",
              }}>
                <Box sx={{
                  background: "linear-gradient(135deg, rgba(0,131,143,0.04) 0%, rgba(0,131,143,0.01) 100%)",
                  borderBottom: "1px solid rgba(0,131,143,0.08)",
                  px: 3, py: 2.5,
                }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box sx={{
                      width: 36, height: 36, borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center",
                      background: "linear-gradient(135deg, rgba(0,131,143,0.12) 0%, rgba(0,131,143,0.06) 100%)",
                    }}>
                      <ImageIcon sx={{ fontSize: 18, color: "#00838f" }} />
                    </Box>
                    <Box>
                      <Typography fontSize={15} fontWeight={700} color="#0f172a">Product Image</Typography>
                      <Typography fontSize={11} color="#94a3b8">Upload a photo of your product</Typography>
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ p: 3 }}>
                  <Box sx={{
                    width: "100%", aspectRatio: "4/3", borderRadius: 3,
                    bgcolor: selectedImage ? "#000" : "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                    background: selectedImage ? "#000" : "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                    border: selectedImage ? "none" : "2px dashed #d1d5db",
                    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                    overflow: "hidden", position: "relative", mb: 2, cursor: "pointer",
                    transition: "all 0.3s ease",
                    "&:hover": selectedImage ? {} : {
                      borderColor: "#00838f", bgcolor: alpha("#00838f", 0.02),
                      "& .upload-icon": { transform: "translateY(-4px)", color: "#00838f" },
                    },
                  }}>
                    {selectedImage ? (
                      <>
                        <img src={URL.createObjectURL(selectedImage)} alt="Preview" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                        <Box sx={{
                          position: "absolute", inset: 0,
                          background: "linear-gradient(180deg, rgba(0,0,0,0.4) 0%, transparent 30%)",
                          opacity: 0, transition: "opacity 0.2s ease",
                          "&:hover": { opacity: 1 },
                          display: "flex", alignItems: "flex-start", justifyContent: "flex-end", p: 1.5,
                        }}>
                          <IconButton size="small" onClick={() => setSelectedImage(undefined)}
                            sx={{
                              bgcolor: "rgba(255,255,255,0.9)", color: "#ef4444",
                              width: 32, height: 32,
                              "&:hover": { bgcolor: "#fff" },
                              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                            }}>
                            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                      </>
                    ) : (
                      <label htmlFor="product-img" style={{ cursor: "pointer", textAlign: "center", width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                        <CloudUploadIcon className="upload-icon" sx={{ fontSize: 40, color: "#cbd5e1", mb: 1.5, transition: "all 0.3s ease" }} />
                        <Typography fontSize={13} color="#64748b" fontWeight={600}>Click to upload</Typography>
                        <Typography fontSize={11} color="#94a3b8" mt={0.5}>JPG, PNG, GIF up to 2MB</Typography>
                      </label>
                    )}
                  </Box>
                  <input accept="image/*" style={{ display: "none" }} id="product-img" type="file" onChange={handleImageChange} />
                  {!selectedImage && (
                    <label htmlFor="product-img">
                      <Button component="span" fullWidth variant="outlined" size="small" startIcon={<CloudUploadIcon sx={{ fontSize: 14 }} />}
                        sx={{
                          borderRadius: 2.5, textTransform: "none", fontSize: 12, fontWeight: 600,
                          borderColor: "#e2e8f0", color: "#64748b", py: 1,
                          "&:hover": { borderColor: "#00838f", color: "#00838f", bgcolor: alpha("#00838f", 0.02) },
                          transition: "all 0.2s ease",
                        }}>
                        Browse Files
                      </Button>
                    </label>
                  )}
                </Box>
              </Paper>

              {/* Sales Channels */}
              <Paper elevation={0} sx={{
                mb: 3, borderRadius: 3, border: "1px solid #e2e8f0", overflow: "hidden",
                "&:hover": { borderColor: "#cbd5e1", boxShadow: "0 4px 20px rgba(0,0,0,0.04)" },
                transition: "all 0.3s ease",
              }}>
                <Box sx={{
                  background: "linear-gradient(135deg, rgba(16,163,127,0.04) 0%, rgba(16,163,127,0.01) 100%)",
                  borderBottom: "1px solid rgba(16,163,127,0.08)",
                  px: 3, py: 2.5,
                }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box sx={{
                      width: 36, height: 36, borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center",
                      background: "linear-gradient(135deg, rgba(16,163,127,0.12) 0%, rgba(16,163,127,0.06) 100%)",
                    }}>
                      <TuneIcon sx={{ fontSize: 18, color: "#10a37f" }} />
                    </Box>
                    <Box>
                      <Typography fontSize={15} fontWeight={700} color="#0f172a">Sales Channels</Typography>
                      <Typography fontSize={11} color="#94a3b8">Where this product is available</Typography>
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ p: 2 }}>
                  {[
                    { label: "Available in Wholesale", field: "productViewOnline", color: "#16a34a", icon: "W" },
                    { label: "Available in POS", field: "productViewPos", color: "#3b82f6", icon: "P" },
                    { label: "Can Sell", field: "productCanSell", color: "#8b5cf6", icon: "S" },
                    { label: "Active", field: "isActive", color: "#0891b2", icon: "A" },
                  ].map(ch => (
                    <Box key={ch.field} sx={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      py: 1.5, px: 1.5, mx: -0.5, borderRadius: 2,
                      transition: "all 0.15s ease",
                      "&:hover": { bgcolor: alpha(ch.color, 0.03) },
                    }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Box sx={{
                          width: 26, height: 26, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                          bgcolor: (formik.values as any)[ch.field] ? alpha(ch.color, 0.1) : "#f1f5f9",
                          transition: "all 0.2s ease",
                        }}>
                          <Typography fontSize={10} fontWeight={800} color={(formik.values as any)[ch.field] ? ch.color : "#94a3b8"}>
                            {ch.icon}
                          </Typography>
                        </Box>
                        <Typography fontSize={13} color="#334155" fontWeight={500}>{ch.label}</Typography>
                      </Box>
                      <Switch size="small" checked={Boolean((formik.values as any)[ch.field])} onChange={(_, v) => formik.setFieldValue(ch.field, v)}
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": { color: ch.color },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: ch.color },
                        }} />
                    </Box>
                  ))}
                </Box>
              </Paper>

              {/* POS Settings */}
              <Paper elevation={0} sx={{
                mb: 3, borderRadius: 3, border: "1px solid #e2e8f0", overflow: "hidden",
                "&:hover": { borderColor: "#cbd5e1", boxShadow: "0 4px 20px rgba(0,0,0,0.04)" },
                transition: "all 0.3s ease",
              }}>
                <Box sx={{
                  background: "linear-gradient(135deg, rgba(99,102,241,0.04) 0%, rgba(99,102,241,0.01) 100%)",
                  borderBottom: "1px solid rgba(99,102,241,0.08)",
                  px: 3, py: 2.5,
                }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box sx={{
                      width: 36, height: 36, borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center",
                      background: "linear-gradient(135deg, rgba(99,102,241,0.12) 0%, rgba(99,102,241,0.06) 100%)",
                    }}>
                      <ColorLensIcon sx={{ fontSize: 18, color: "#6366f1" }} />
                    </Box>
                    <Box>
                      <Typography fontSize={15} fontWeight={700} color="#0f172a">POS Settings</Typography>
                      <Typography fontSize={11} color="#94a3b8">Button appearance and modifiers</Typography>
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ p: 3 }}>
                  <Typography sx={{ ...labelSx, fontSize: 11 }}>Button Color</Typography>
                  <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", mb: 2 }}>
                    <Box
                      onClick={() => setShowColorPicker(v => !v)}
                      sx={{
                        width: 40, height: 40, borderRadius: 2.5, bgcolor: selectedColor,
                        border: "2px solid #e2e8f0", cursor: "pointer",
                        boxShadow: `0 2px 8px ${alpha(selectedColor, 0.3)}`,
                        transition: "all 0.2s ease",
                        "&:hover": { transform: "scale(1.08)", boxShadow: `0 4px 16px ${alpha(selectedColor, 0.4)}` },
                      }}
                    />
                    <TextField size="small" value={selectedColor} onChange={e => setSelectedColor(e.target.value)} sx={{ flex: 1, ...inputSx }} />
                  </Box>
                  <Collapse in={showColorPicker}>
                    <Box ref={colorRef} sx={{ mb: 2, borderRadius: 2, overflow: "hidden" }}>
                      <HexColorPicker color={selectedColor} onChange={setSelectedColor} style={{ width: "100%" }} />
                    </Box>
                  </Collapse>

                  <Divider sx={{ my: 2, borderColor: "#f1f5f9" }} />

                  <Typography sx={{ ...labelSx, fontSize: 11 }}>Has Modifier</Typography>
                  <Box sx={{
                    display: "flex", alignItems: "center", gap: 1.5,
                    p: 1.5, borderRadius: 2,
                    bgcolor: formik.values.isModifier ? alpha("#16a34a", 0.04) : "#fafafa",
                    border: `1px solid ${formik.values.isModifier ? alpha("#16a34a", 0.15) : "#f1f5f9"}`,
                    transition: "all 0.2s ease",
                  }}>
                    <Switch size="small" checked={formik.values.isModifier as boolean} onChange={(_, v) => formik.setFieldValue("isModifier", v)} />
                    <Typography fontSize={12} color={formik.values.isModifier ? "#16a34a" : "#94a3b8"} fontWeight={600}>{formik.values.isModifier ? "Yes" : "No"}</Typography>
                  </Box>
                  {formik.values.isModifier && modifierGroupList?.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography sx={{ ...labelSx, fontSize: 11 }}>Modifier Group</Typography>
                      <TextField select fullWidth size="small" value={formik.values.modifierGroupId?.[0] || ""} onChange={e => formik.setFieldValue("modifierGroupId", e.target.value ? [Number(e.target.value)] : [])} sx={inputSx}>
                        <MenuItem value="">None</MenuItem>
                        {modifierGroupList.map(g => <MenuItem key={g.id} value={g.id}>{g.modifierGroupName}</MenuItem>)}
                      </TextField>
                    </Box>
                  )}
                </Box>
              </Paper>

              {/* Advanced */}
              <Paper elevation={0} sx={{
                mb: 3, borderRadius: 3, border: "1px solid #e2e8f0", overflow: "hidden",
                "&:hover": { borderColor: "#cbd5e1", boxShadow: "0 4px 20px rgba(0,0,0,0.04)" },
                transition: "all 0.3s ease",
              }}>
                <Box sx={{
                  background: showAdvanced
                    ? "linear-gradient(135deg, rgba(100,116,139,0.06) 0%, rgba(100,116,139,0.02) 100%)"
                    : "linear-gradient(135deg, rgba(100,116,139,0.03) 0%, rgba(100,116,139,0.01) 100%)",
                  px: 3, py: 2.5, cursor: "pointer",
                  transition: "all 0.2s ease",
                  "&:hover": { background: "linear-gradient(135deg, rgba(100,116,139,0.06) 0%, rgba(100,116,139,0.02) 100%)" },
                }}
                  onClick={() => setShowAdvanced(v => !v)}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box sx={{
                      width: 36, height: 36, borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center",
                      background: "linear-gradient(135deg, rgba(100,116,139,0.12) 0%, rgba(100,116,139,0.06) 100%)",
                    }}>
                      <SettingsIcon sx={{ fontSize: 18, color: "#64748b" }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography fontSize={15} fontWeight={700} color="#0f172a">Advanced</Typography>
                      <Typography fontSize={11} color="#94a3b8">Barcodes, serialization, and tags</Typography>
                    </Box>
                    <ExpandMoreIcon sx={{
                      fontSize: 20, color: "#94a3b8",
                      transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      transform: showAdvanced ? "rotate(180deg)" : "rotate(0)",
                    }} />
                  </Box>
                </Box>
                <Collapse in={showAdvanced}>
                  <Box sx={{ p: 3, borderTop: "1px solid #f1f5f9" }}>
                    <Grid container spacing={2.5}>
                      <Grid item xs={12} sm={6}>
                        <Typography sx={{ ...labelSx, fontSize: 11 }}>System Barcode</Typography>
                        <TextField fullWidth size="small" placeholder="Auto-generated" {...formik.getFieldProps("productBarcode")}
                          InputProps={{
                            startAdornment: <QrCodeIcon sx={{ fontSize: 16, color: "#94a3b8", mr: 1 }} />,
                          }}
                          sx={inputSx} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography sx={{ ...labelSx, fontSize: 11 }}>Manual Barcode</Typography>
                        <TextField fullWidth size="small" placeholder="Scan or enter" {...formik.getFieldProps("productManualBarcode")}
                          InputProps={{
                            startAdornment: <QrCodeIcon sx={{ fontSize: 16, color: "#94a3b8", mr: 1 }} />,
                          }}
                          sx={inputSx} />
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{
                          display: "flex", alignItems: "center", gap: 1.5,
                          p: 1.5, borderRadius: 2,
                          bgcolor: formik.values.isSerialize ? alpha("#8b5cf6", 0.04) : "#fafafa",
                          border: `1px solid ${formik.values.isSerialize ? alpha("#8b5cf6", 0.15) : "#f1f5f9"}`,
                          transition: "all 0.2s ease",
                        }}>
                          <Switch size="small" checked={formik.values.isSerialize as boolean} onChange={(_, v) => formik.setFieldValue("isSerialize", v)}
                            sx={{
                              "& .MuiSwitch-switchBase.Mui-checked": { color: "#8b5cf6" },
                              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: "#8b5cf6" },
                            }}
                          />
                          <Box>
                            <Typography fontSize={13} color="#334155" fontWeight={500}>Serialized Product</Typography>
                            <Typography fontSize={10} color="#94a3b8">Track individual units by serial number</Typography>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography sx={{ ...labelSx, fontSize: 11 }}>Tags</Typography>
                        <TextField select fullWidth size="small" SelectProps={{ multiple: true }} value={formik.values.productTagIds || []} onChange={e => formik.setFieldValue("productTagIds", e.target.value)} sx={inputSx}>
                          {productTagList?.map(t => <MenuItem key={t.id} value={t.id}>{t.tagName}</MenuItem>)}
                        </TextField>
                      </Grid>
                    </Grid>
                  </Box>
                </Collapse>
              </Paper>

              {/* Save Button */}
              <Paper elevation={0} sx={{
                p: 3, borderRadius: 3, border: "1px solid #e2e8f0",
                background: "linear-gradient(135deg, #f8fafc 0%, #fff 100%)",
              }}>
                <Button fullWidth variant="contained" disableElevation disabled={!formValid || isLoading}
                  onClick={() => formik.handleSubmit()}
                  startIcon={isLoading ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <CheckCircleOutlineIcon sx={{ fontSize: 20 }} />}
                  sx={{
                    borderRadius: 3, py: 1.8, textTransform: "none", fontWeight: 700, fontSize: 15,
                    background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
                    boxShadow: "0 4px 20px rgba(15,23,42,0.25)",
                    "&:hover": {
                      background: "linear-gradient(135deg, #1e293b 0%, #334155 100%)",
                      boxShadow: "0 6px 30px rgba(15,23,42,0.3)",
                      transform: "translateY(-1px)",
                    },
                    "&.Mui-disabled": { background: "#e2e8f0", color: "#94a3b8", boxShadow: "none" },
                    transition: "all 0.2s ease",
                  }}>
                  {isLoading ? "Saving..." : "Save Product"}
                </Button>
              </Paper>
            </Grid>
          </Grid>
        </form>
      </Box>
    </Box>
  );
};

export default Index;
